import { MEETING_IMPORT_CONTRACT as contract } from './meetingImportContract.ts';
import {
  mergeMeetingCandidates,
  validateMeetingItems,
  validateMeetingSource,
  validateMeetingTranscript,
  validateMeetingDate,
  MEETING_MAX_TRANSCRIPT_CHUNKS,
  type MeetingCandidate,
  type MeetingCommitItem,
  type MeetingImport,
  type MeetingSource,
} from './meetingImport.ts';
export type MeetingDestination =
  | { kind: 'area' | 'project' | 'resource'; id: string; newName?: never }
  | { kind: 'area'; newName: string; id?: never };
export interface MeetingSubmission {
  items: MeetingCommitItem[];
  container: MeetingDestination;
}
export interface MeetingLocalEntry {
  id: string;
  ownerId: string;
  source: MeetingSource;
  createPending: boolean;
  uploadPending: boolean;
  commit?: MeetingSubmission;
}
export interface MeetingLocalStorage {
  read(ownerId: string): Promise<MeetingLocalEntry[]>;
  mutate(
    ownerId: string,
    change: (entries: MeetingLocalEntry[]) => MeetingLocalEntry[],
  ): Promise<MeetingLocalEntry[]>;
}
export interface MeetingReceiptItem {
  candidate_id: string;
  capture_id: string;
  destination_id: string;
  kind: 'task' | 'note';
  container_kind: 'area' | 'project' | 'resource';
  container_id: string;
}
export interface MeetingReceipt {
  import_id: string;
  items: MeetingReceiptItem[];
}
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const uuid = (v: unknown): v is string =>
  typeof v === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
function identity(owner: string, id?: string) {
  if (!uuid(owner) || (id !== undefined && !uuid(id)))
    throw new Error('Invalid meeting owner or import identity');
}
function destination(value: MeetingDestination): MeetingDestination {
  if (
    !record(value) ||
    !['area', 'project', 'resource'].includes(String(value.kind))
  )
    throw new Error('Choose a meeting destination');
  if (value.newName !== undefined) {
    if (
      value.kind !== 'area' ||
      value.id !== undefined ||
      typeof value.newName !== 'string' ||
      !value.newName.trim() ||
      Array.from(value.newName).length > contract.max_container_name_chars
    )
      throw new Error('Name a new Area');
    return { kind: 'area', newName: value.newName };
  }
  if (!uuid(value.id)) throw new Error('Choose a meeting destination');
  return { kind: value.kind, id: value.id };
}
export function validateMeetingReceipt(
  raw: unknown,
  id: string,
  submission?: MeetingSubmission,
): MeetingReceipt {
  if (
    !record(raw) ||
    raw.import_id !== id ||
    !Array.isArray(raw.items) ||
    !raw.items.length ||
    raw.items.length > contract.max_candidates
  )
    throw new Error('Meeting filing receipt did not match');
  const ids = new Set<string>(),
    destinations = new Set<string>(),
    captures = new Set<string>();
  const items = raw.items.map((item) => {
    if (
      !record(item) ||
      typeof item.candidate_id !== 'string' ||
      !item.candidate_id ||
      ids.has(item.candidate_id) ||
      !uuid(item.capture_id) ||
      captures.has(item.capture_id) ||
      !uuid(item.destination_id) ||
      destinations.has(item.destination_id) ||
      !['task', 'note'].includes(String(item.kind)) ||
      !['project', 'area', 'resource'].includes(String(item.container_kind)) ||
      (item.kind === 'task' && item.container_kind === 'resource') ||
      !uuid(item.container_id)
    )
      throw new Error('Invalid meeting filing receipt');
    ids.add(item.candidate_id);
    captures.add(item.capture_id);
    destinations.add(item.destination_id);
    return {
      candidate_id: item.candidate_id,
      capture_id: item.capture_id,
      destination_id: item.destination_id,
      kind: item.kind,
      container_kind: item.container_kind,
      container_id: item.container_id,
    } as MeetingReceiptItem;
  });
  if (submission) {
    const selected = submission.items.filter((i) => i.selected);
    if (
      selected.length !== items.length ||
      selected.some(
        (c) =>
          !items.some(
            (i) =>
              i.candidate_id === c.id &&
              i.kind === c.kind &&
              i.container_kind === submission.container.kind &&
              (submission.container.id === undefined ||
                i.container_id === submission.container.id),
          ),
      ) ||
      new Set(items.map((i) => i.container_id)).size !== 1
    )
      throw new Error('Meeting filing receipt did not match the submission');
  }
  return { import_id: id, items };
}
export function validateMeetingImport(
  raw: unknown,
  owner: string,
  id?: string,
): MeetingImport {
  identity(owner, id);
  if (
    !record(raw) ||
    !uuid(raw.id) ||
    (id !== undefined && raw.id !== id) ||
    raw.user_id !== owner ||
    !['pending', 'extracting', 'review', 'filed'].includes(
      String(raw.status),
    ) ||
    typeof raw.created_at !== 'string' ||
    !Number.isFinite(Date.parse(raw.created_at)) ||
    !Number.isSafeInteger(raw.next_chunk) ||
    Number(raw.next_chunk) < 0 ||
    Number(raw.next_chunk) > MEETING_MAX_TRANSCRIPT_CHUNKS ||
    (raw.error !== null && typeof raw.error !== 'string') ||
    !Array.isArray(raw.candidates)
  )
    throw new Error('Invalid meeting import response or owner');
  const source = validateMeetingSource(raw.source, owner, raw.id);
  const transcript =
    raw.transcript === null ? null : validateMeetingTranscript(raw.transcript);
  const canonical = mergeMeetingCandidates([], raw.candidates);
  const candidates: MeetingCommitItem[] = canonical.map((item, index) => {
    const personIds = (raw.candidates as Record<string, unknown>[])[index]
      .personIds;
    if (personIds === undefined) return item;
    if (
      !Array.isArray(personIds) ||
      personIds.length > contract.max_people_per_item ||
      !personIds.every(uuid) ||
      new Set(personIds).size !== personIds.length
    )
      throw new Error('Invalid saved meeting People choices');
    return { ...item, personIds };
  });
  if (
    candidates.length !== raw.candidates.length ||
    (transcript === null &&
      (candidates.length ||
        ['review', 'filed', 'extracting'].includes(String(raw.status)))) ||
    (transcript !== null &&
      candidates.some(
        (c) =>
          Array.from(transcript).slice(c.sourceStart, c.sourceEnd).join('') !==
          c.quote,
      ))
  )
    throw new Error(
      'Meeting candidate provenance did not match the transcript',
    );
  const receipt =
    raw.status === 'filed' ? validateMeetingReceipt(raw.receipt, raw.id) : null;
  if (raw.status !== 'filed' && raw.receipt !== null)
    throw new Error('Unexpected meeting filing receipt');
  if (raw.meeting_date !== undefined && raw.meeting_date !== null)
    validateMeetingDate(raw.meeting_date);
  return {
    id: raw.id,
    user_id: owner,
    source,
    transcript,
    status: raw.status as MeetingImport['status'],
    candidates,
    next_chunk: Number(raw.next_chunk),
    error: raw.error as string | null,
    receipt,
    created_at: raw.created_at,
    ...(raw.meeting_date === undefined
      ? {}
      : { meeting_date: raw.meeting_date as string | null }),
  };
}
export function parseMeetingLocalEntries(
  raw: unknown,
  owner: string,
): MeetingLocalEntry[] {
  identity(owner);
  if (!Array.isArray(raw))
    throw new Error(
      'Meeting recovery storage is invalid; existing sources are kept',
    );
  const seen = new Set<string>();
  return raw.map((entry) => {
    if (
      !record(entry) ||
      !uuid(entry.id) ||
      entry.ownerId !== owner ||
      seen.has(entry.id) ||
      typeof entry.createPending !== 'boolean' ||
      typeof entry.uploadPending !== 'boolean'
    )
      throw new Error(
        'Meeting recovery identity is invalid; existing sources are kept',
      );
    seen.add(entry.id);
    const source = validateMeetingSource(entry.source, owner, entry.id);
    if (entry.uploadPending && source.kind !== 'media')
      throw new Error('Invalid pending meeting upload');
    let commit: MeetingSubmission | undefined;
    if (entry.commit !== undefined) {
      if (!record(entry.commit) || !Array.isArray(entry.commit.items))
        throw new Error('Invalid retained meeting submission');
      commit = {
        items: validateMeetingItems(entry.commit.items, entry.commit.items),
        container: destination(entry.commit.container as MeetingDestination),
      };
    }
    return {
      id: entry.id,
      ownerId: owner,
      source,
      createPending: entry.createPending,
      uploadPending: entry.uploadPending,
      ...(commit ? { commit } : {}),
    };
  });
}
/** Structured PostgREST evidence, kept separate from transport failures and error text. */
export class MeetingRpcError extends Error {
  readonly evidence: {
    code: string;
    message: string;
    details: string | null;
    hint: string | null;
    status: number;
  };
  constructor(evidence: {
    code: string;
    message: string;
    details: string | null;
    hint: string | null;
    status: number;
  }) {
    super(evidence.message);
    this.name = 'MeetingRpcError';
    this.evidence = evidence;
  }
}
export type MeetingReviewDraft = Omit<MeetingCommitItem, 'people'> & {
  peopleText: string;
};
export const meetingReviewDraft = ({
  people,
  ...item
}: MeetingCommitItem): MeetingReviewDraft => ({
  ...item,
  peopleText: people.join(', '),
});
export const meetingReviewItem = ({
  peopleText,
  ...item
}: MeetingReviewDraft): MeetingCommitItem => ({
  ...item,
  people: peopleText
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean),
});

export interface MeetingPersonMatch {
  id: string;
  user_id: string;
  name: string;
  context: string | null;
  created_at: string;
}
export interface MeetingPeopleMatches {
  name: string;
  matches: MeetingPersonMatch[];
}
/** SQL alone decides name equivalence; each response retains the exact requested spelling. */
export function validateMeetingPeopleMatches(
  raw: unknown,
  owner: string,
  names: string[],
): MeetingPeopleMatches[] {
  identity(owner);
  if (!Array.isArray(raw) || raw.length !== names.length)
    throw new Error('Incomplete meeting People lookup');
  return names.map((name, index) => {
    const result = raw[index];
    if (
      !record(result) ||
      result.name !== name ||
      !Array.isArray(result.matches)
    )
      throw new Error('Invalid meeting People lookup');
    const matches = result.matches.map((person: unknown) => {
      if (
        !record(person) ||
        person.user_id !== owner ||
        typeof person.id !== 'string' ||
        !uuid(person.id) ||
        typeof person.name !== 'string' ||
        (person.context !== null && typeof person.context !== 'string') ||
        typeof person.created_at !== 'string' ||
        !Number.isFinite(Date.parse(person.created_at))
      )
        throw new Error('Invalid owned meeting Person');
      return person as unknown as MeetingPersonMatch;
    });
    if (new Set(matches.map((p) => p.id)).size !== matches.length)
      throw new Error('Duplicate meeting Person identity');
    return { name, matches };
  });
}
export interface MeetingClientDependencies {
  storage: MeetingLocalStorage;
  assertOwner(ownerId: string): Promise<void>;
  invoke(body: Record<string, unknown>, ownerId: string): Promise<unknown>;
  rpc(args: Record<string, unknown>, ownerId: string): Promise<unknown>;
  /** Must verify the private object's path, bytes/size and content type before resolving. */
  upload(
    source: Extract<MeetingSource, { kind: 'media' }>,
    ownerId: string,
    id: string,
  ): Promise<void>;
}
export function createMeetingImportClient(deps: MeetingClientDependencies) {
  const read = async (owner: string) =>
    parseMeetingLocalEntries(await deps.storage.read(owner), owner);
  const mutate = (
    owner: string,
    change: (entries: MeetingLocalEntry[]) => MeetingLocalEntry[],
  ) =>
    deps.storage.mutate(owner, (entries) =>
      parseMeetingLocalEntries(
        change(parseMeetingLocalEntries(entries, owner)),
        owner,
      ),
    );
  const request = async (owner: string, body: Record<string, unknown>) => {
    identity(owner, body.id as string | undefined);
    await deps.assertOwner(owner);
    const raw = await deps.invoke(body, owner);
    await deps.assertOwner(owner);
    if (!record(raw)) throw new Error('Invalid meeting response');
    return raw;
  };
  const single = async (owner: string, body: Record<string, unknown>) =>
    validateMeetingImport(
      (await request(owner, body)).import,
      owner,
      body.id as string,
    );
  async function createMeetingImport(
    owner: string,
    id: string,
    input: MeetingSource,
  ): Promise<MeetingImport> {
    identity(owner, id);
    await deps.assertOwner(owner);
    const source = validateMeetingSource(input, owner, id);
    await mutate(owner, (entries) => {
      const previous = entries.find((e) => e.id === id);
      if (previous) {
        if (JSON.stringify(previous.source) !== JSON.stringify(source))
          throw new Error('Meeting source conflicts with its saved identity');
        return entries;
      }
      return [
        ...entries,
        {
          id,
          ownerId: owner,
          source,
          createPending: true,
          uploadPending: source.kind === 'media',
        },
      ];
    });
    const saved = await single(owner, { action: 'create', id, source });
    if (JSON.stringify(saved.source) !== JSON.stringify(source))
      throw new Error('Server did not retain the original meeting source');
    await mutate(owner, (entries) =>
      entries.map((e) => (e.id === id ? { ...e, createPending: false } : e)),
    );
    const pending = (await read(owner)).find((e) => e.id === id);
    if (pending?.uploadPending && source.kind === 'media') {
      await deps.assertOwner(owner);
      await deps.upload(source, owner, id);
      await deps.assertOwner(owner);
      await mutate(owner, (entries) =>
        entries.map((e) => (e.id === id ? { ...e, uploadPending: false } : e)),
      );
    }
    return saved;
  }
  const getMeetingImport = (owner: string, id: string) =>
    single(owner, { action: 'get', id });
  async function listMeetingImports(owner: string) {
    const raw = await request(owner, { action: 'get' });
    if (!Array.isArray(raw.imports))
      throw new Error('Invalid meeting import list');
    const rows = raw.imports.map((r) => validateMeetingImport(r, owner));
    if (new Set(rows.map((r) => r.id)).size !== rows.length)
      throw new Error('Duplicate meeting import identity');
    return rows;
  }
  async function processMeetingImport(
    owner: string,
    id: string,
    onProgress?: (row: MeetingImport) => void,
  ) {
    const pending = (await read(owner)).find((e) => e.id === id);
    let row =
      pending && (pending.createPending || pending.uploadPending)
        ? await createMeetingImport(owner, id, pending.source)
        : await getMeetingImport(owner, id);
    onProgress?.(row);
    // URL recording retention may leave the projected phase unchanged once, before transcription.
    let retainedUrlPhase = false;
    for (
      let count = 0;
      row.status !== 'review' && row.status !== 'filed';
      count++
    ) {
      if (count >= MEETING_MAX_TRANSCRIPT_CHUNKS + 2)
        throw new Error(
          'Meeting processing did not complete; progress is retained. Resume to reconcile.',
        );
      const previous = row;
      row = await single(owner, { action: 'process', id });
      if (row.error) throw new Error(row.error);
      if (
        row.next_chunk < previous.next_chunk ||
        (previous.transcript !== null && row.transcript !== previous.transcript)
      )
        throw new Error(
          'Meeting processing returned inconsistent source or progress',
        );
      const advanced =
        row.next_chunk > previous.next_chunk ||
        (previous.status === 'pending' && row.status !== 'pending') ||
        (previous.transcript === null && row.transcript !== null) ||
        row.status === 'review' ||
        row.status === 'filed';
      if (!advanced) {
        if (
          !retainedUrlPhase &&
          previous.status === 'pending' &&
          previous.transcript === null &&
          previous.source.kind === 'url'
        )
          retainedUrlPhase = true;
        else
          throw new Error(
            'Meeting processing did not advance; source and progress are retained. Resume to reconcile.',
          );
      }
      onProgress?.(row);
    }
    return row;
  }
  async function updateMeetingReview(
    owner: string,
    id: string,
    candidates: MeetingCandidate[],
  ) {
    if ((await read(owner)).find((e) => e.id === id)?.commit)
      throw new Error('Reconcile the saved filing submission before editing');
    const current = await getMeetingImport(owner, id);
    const valid = validateMeetingItems(candidates, current.candidates);
    const saved = await single(owner, {
      action: 'review',
      id,
      candidates: valid,
    });
    if (
      saved.status !== 'review' ||
      JSON.stringify(saved.candidates) !== JSON.stringify(valid)
    )
      throw new Error(
        'Meeting review save receipt did not match the edits; changes are still unsaved',
      );
    return saved;
  }
  async function commitMeetingImport(
    owner: string,
    id: string,
    items: MeetingCommitItem[],
    container: MeetingDestination,
  ) {
    identity(owner, id);
    await deps.assertOwner(owner);
    let entry = (await read(owner)).find((e) => e.id === id);
    if (!entry?.commit) {
      const row = await getMeetingImport(owner, id);
      const submission = {
        items: validateMeetingItems(items, row.candidates),
        container: destination(container),
      };
      if (
        submission.container.kind === 'resource' &&
        submission.items.some((i) => i.selected && i.kind === 'task')
      )
        throw new Error('Tasks need a Project or Area');
      const entries = await mutate(owner, (entries) => {
        const existing = entries.find((e) => e.id === id);
        if (existing?.commit) return entries;
        return [
          ...entries.filter((e) => e.id !== id),
          {
            ...(existing ?? {
              id,
              ownerId: owner,
              source: row.source,
              createPending: false,
              uploadPending: false,
            }),
            commit: clone(submission),
          },
        ];
      });
      entry = entries.find((e) => e.id === id);
    }
    if (!entry?.commit)
      throw new Error('Meeting submission could not be retained');
    const submission = entry.commit;
    await deps.assertOwner(owner);
    let rawReceipt: unknown;
    try {
      rawReceipt = await deps.rpc(
        {
          p_import_id: id,
          p_items: submission.items,
          p_container_kind: submission.container.kind,
          p_container_id: submission.container.id ?? null,
          p_new_container_name: submission.container.newName ?? null,
        },
        owner,
      );
    } catch (error) {
      // PostgREST maps invalid-parameter 22023 to 400 and missing-target P0002 to 500.
      // A fresh review row also excludes a prior successful, differently submitted commit.
      // Transport errors, other SQL states and receipt validation failures retain custody.
      if (
        error instanceof MeetingRpcError &&
        ((error.evidence.status === 400 && error.evidence.code === '22023') ||
          (error.evidence.status === 500 && error.evidence.code === 'P0002'))
      ) {
        const current = await getMeetingImport(owner, id);
        if (current.status === 'review') {
          await mutate(owner, (entries) =>
            entries.map((saved) => {
              if (
                saved.id !== id ||
                JSON.stringify(saved.commit) !== JSON.stringify(submission)
              )
                return saved;
              const editable = { ...saved };
              delete editable.commit;
              return editable;
            }),
          );
        }
      }
      throw error;
    }
    const receipt = validateMeetingReceipt(rawReceipt, id, submission);
    await deps.assertOwner(owner);
    const filed = await getMeetingImport(owner, id);
    if (
      filed.status !== 'filed' ||
      JSON.stringify(filed.receipt) !== JSON.stringify(receipt)
    )
      throw new Error('Meeting filing receipt is awaiting reconciliation');
    await mutate(owner, (entries) => entries.filter((e) => e.id !== id));
    return { receipt, import: filed };
  }
  return {
    createMeetingImport,
    getMeetingImport,
    listMeetingImports,
    processMeetingImport,
    updateMeetingReview,
    commitMeetingImport,
    pendingMeetingImports: async (owner: string) => {
      await deps.assertOwner(owner);
      return read(owner);
    },
  };
}
export type MeetingImportClient = ReturnType<typeof createMeetingImportClient>;
/** Byte bounds are checked before reading. Text is then bounded by Unicode code points. */
export function meetingFileAdmission(
  name: string,
  mimeType: string,
  size: number,
): { kind: 'transcript' } | { kind: 'media'; mimeType: string } {
  if (
    !name ||
    /[/\\]/.test(name) ||
    Array.from(name).length > contract.max_filename_chars ||
    !Number.isSafeInteger(size) ||
    size <= 0
  )
    throw new Error('Choose a nonempty supported meeting file');
  const extension = name.split('.').pop()?.toLowerCase();
  if (extension && ['txt', 'vtt', 'srt'].includes(extension)) {
    if (size > contract.max_transcript_chars * 4)
      throw new Error(
        `Transcript files must be at most ${(contract.max_transcript_chars * 4) / 1_000_000} MB and ${contract.max_transcript_chars.toLocaleString('en-US')} characters`,
      );
    return { kind: 'transcript' };
  }
  if (!contract.media_extensions.some((e) => e === extension))
    throw new Error('Choose TXT, VTT, SRT or a supported meeting recording');
  if (size > contract.max_media_bytes)
    throw new Error(
      `Recordings must be at most ${contract.max_media_bytes / 1_000_000} MB`,
    );
  // Picker MIME metadata is sometimes absent. This is a format translation, not an alternate supported-format catalog.
  const translated: Record<string, string> = {
    mp3: 'audio/mpeg',
    mpga: 'audio/mpga',
    m4a: 'audio/mp4',
    mp4: 'video/mp4',
    mpeg: 'video/mpeg',
    flac: 'audio/flac',
    ogg: 'audio/ogg',
    wav: 'audio/wav',
    webm: 'audio/webm',
  };
  const admitted = mimeType || translated[extension!];
  if (!contract.media_mime_types.some((type) => type === admitted))
    throw new Error('The recording MIME type is not supported');
  return { kind: 'media', mimeType: admitted };
}
